import { v4 as uuid } from 'uuid';
import { deleteOwnedMedia, uploadOwnedMedia } from '../../util/ownedMedia';

function getExistingPortfolioKey(image) {
  if (typeof image === 'string') return image;
  return image?.url || image?.processedUrl || null;
}

function getFileName(file) {
  return file?.name || 'portfolio-image.jpg';
}

async function cleanupUploadedPortfolioImages({
  uploadedMedia,
  deleteMedia,
}) {
  if (!uploadedMedia.length) return null;

  const cleanupResults = await Promise.allSettled(
    uploadedMedia.map((media) => deleteMedia(media))
  );

  return cleanupResults.find((result) => result.status === 'rejected')?.reason || null;
}

export function resolveCreativePackageId({
  isEditing,
  packageId,
  packageData,
  idGenerator = uuid,
}) {
  if (isEditing) {
    return packageData?.id || packageId;
  }

  return idGenerator();
}

export async function prepareCreativePackagePortfolioImages({
  creativeId,
  packageId,
  portfolioImages,
  uploadMedia = uploadOwnedMedia,
  uploadedMedia = [],
}) {
  if (!creativeId) {
    throw new Error('Creative-Profil konnte nicht zugeordnet werden.');
  }
  if (!packageId) {
    throw new Error('Paket konnte nicht zugeordnet werden.');
  }

  const imageKeys = [];

  for (const image of Array.isArray(portfolioImages) ? portfolioImages : []) {
    if (image?.file) {
      const uploadResult = await uploadMedia({
        file: image.file,
        entityType: 'CREATIVE_PACKAGE',
        entityId: packageId,
        parentEntityId: creativeId,
        mediaKind: 'IMAGE',
        slot: 'portfolio',
        fileName: getFileName(image.file),
      });

      const media = {
        key: uploadResult.key,
        entityType: 'CREATIVE_PACKAGE',
        entityId: packageId,
        parentEntityId: creativeId,
        mediaKind: 'IMAGE',
      };
      uploadedMedia.push(media);
      imageKeys.push(uploadResult.key);
      continue;
    }

    const existingKey = getExistingPortfolioKey(image);
    if (existingKey) {
      imageKeys.push(existingKey);
    }
  }

  return imageKeys;
}

export async function saveCreativePackageWithOwnedPortfolioImages({
  creative,
  isEditing,
  packageId,
  packageData,
  portfolioImages,
  buildPackageData,
  handleCreatePackage,
  handleUpdatePackage,
  syncPackageProjectLinks,
  uploadMedia = uploadOwnedMedia,
  deleteMedia = deleteOwnedMedia,
  idGenerator = uuid,
}) {
  const resolvedPackageId = resolveCreativePackageId({
    isEditing,
    packageId,
    packageData,
    idGenerator,
  });
  const uploadedMedia = [];
  let savedPackage;

  try {
    const portfolioImageKeys = await prepareCreativePackagePortfolioImages({
      creativeId: creative?.id,
      packageId: resolvedPackageId,
      portfolioImages,
      uploadMedia,
      uploadedMedia,
    });

    const packageDataToSave = buildPackageData({
      packageId: resolvedPackageId,
      portfolioImages: portfolioImageKeys,
    });

    savedPackage = isEditing
      ? await handleUpdatePackage(packageDataToSave)
      : await handleCreatePackage(packageDataToSave);
  } catch (error) {
    const cleanupError = await cleanupUploadedPortfolioImages({
      uploadedMedia,
      deleteMedia,
    });

    if (cleanupError) {
      error.cleanupError = cleanupError;
    }

    throw error;
  }

  await syncPackageProjectLinks?.(savedPackage?.id || resolvedPackageId);

  return savedPackage;
}
